import dayjs from 'dayjs';
import 'dayjs/locale/es.js';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter.js';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore.js';
import type { TimeMode, MessageTimeMode } from '@/types/settings';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.locale('es');

/**
 * Check if a date is on the weekend (Saturday or Sunday).
 */
export function isWeekend(dateStr?: string): boolean {
  const d = dateStr ? dayjs(dateStr) : dayjs();
  const day = d.day();
  return day === 0 || day === 6;
}

/**
 * Get contextual greeting (dynamic with weekend/hour detection or plain prefix).
 */
export function getGreeting(
  dateStr?: string,
  mode: 'dynamic' | 'plain' = 'dynamic',
  customPrefix: string = 'Hola'
): string {
  if (mode === 'plain') {
    return customPrefix || 'Hola';
  }

  if (isWeekend(dateStr)) {
    return 'Hola, feliz fin de semana';
  }

  const hour = dayjs().hour();
  if (hour < 12) return 'Hola, buenos días';
  if (hour < 19) return 'Hola, buenas tardes';
  return 'Hola, buenas noches';
}

/**
 * Grammatical article for hour in Spanish ("a la 1:00 PM" vs "a las 2:00 PM").
 */
export function hourArticle(time12: string): string {
  const clean = time12.trim();
  return /^(0?1):/i.test(clean) ? 'a la' : 'a las';
}

/**
 * Convert 24h time string (HH:mm or HH:mm:ss) to 12h format (e.g. 2:30 PM).
 */
export function to12h(time: string): string {
  if (!time) return '';
  const [h, m] = time.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 || 12;
  return `${hour12}:${String(m || 0).padStart(2, '0')} ${period}`;
}

/**
 * Format a time string based on user's preferred format (12h/24h).
 */
export function formatTime(time: string, mode: TimeMode = '12h'): string {
  if (!time) return '';
  if (mode === '12h') return to12h(time);
  const parts = time.split(':');
  return `${parts[0]}:${parts[1] || '00'}`;
}

/**
 * Format friendly date for WhatsApp messages with customizable flags.
 */
export function formatFriendlyDate(
  dateStr: string,
  options: {
    useFriendlyDate?: boolean;
    includeDayName?: boolean;
    useTomorrowTodayLabels?: boolean;
  } = {}
): string {
  const {
    useFriendlyDate = true,
    includeDayName = true,
    useTomorrowTodayLabels = true,
  } = options;

  if (!useFriendlyDate) return dateStr;

  const d = dayjs(dateStr);
  const today = dayjs();
  const diff = d.startOf('day').diff(today.startOf('day'), 'day');

  const dayNum = d.format('D');
  const monthName = d.format('MMMM');

  if (useTomorrowTodayLabels) {
    if (diff === 0) return `Hoy ${dayNum} de ${monthName}`;
    if (diff === 1) return `Mañana ${dayNum} de ${monthName}`;
    if (diff === -1) return `Ayer ${dayNum} de ${monthName}`;
  }

  if (includeDayName) {
    const dayOfWeek = d.format('dddd');
    const capitalized = dayOfWeek.charAt(0).toUpperCase() + dayOfWeek.slice(1);
    return `${capitalized} ${dayNum} de ${monthName}`;
  }

  return `${dayNum} de ${monthName}`;
}

/**
 * Format date and time with intelligent contextual connectors ("para hoy...", "para mañana...", "para el miércoles...").
 */
export function formatContextualDateConnector(
  dateStr: string,
  timeFormatted: string = '',
  connector: 'para' | 'el' | 'en' = 'para',
  options: {
    useFriendlyDate?: boolean;
    includeDayName?: boolean;
    useTomorrowTodayLabels?: boolean;
  } = {}
): string {
  if (!dateStr) return timeFormatted.trim();
  const d = dayjs(dateStr);
  if (!d.isValid()) return timeFormatted.trim();

  const {
    useTomorrowTodayLabels = true,
    includeDayName = true,
  } = options;

  const today = dayjs();
  const diff = d.startOf('day').diff(today.startOf('day'), 'day');
  const dayNum = d.format('D');
  const monthName = d.format('MMMM');
  const timePart = timeFormatted ? ` ${timeFormatted.trim()}` : '';

  if (useTomorrowTodayLabels) {
    if (diff === 0) {
      const dateText = `hoy ${dayNum} de ${monthName}`;
      return connector === 'para' ? `para ${dateText}${timePart}` : `${dateText}${timePart}`;
    }
    if (diff === 1) {
      const dateText = `mañana ${dayNum} de ${monthName}`;
      return connector === 'para' ? `para ${dateText}${timePart}` : `${dateText}${timePart}`;
    }
    if (diff === -1) {
      const dateText = `ayer ${dayNum} de ${monthName}`;
      return connector === 'para' ? `para ${dateText}${timePart}` : `${dateText}${timePart}`;
    }
  }

  const dayOfWeek = includeDayName ? `${d.format('dddd')} ` : '';
  const dateText = `${dayOfWeek}${dayNum} de ${monthName}`;
  return connector === 'para' ? `para el ${dateText}${timePart}` : `el ${dateText}${timePart}`;
}


/**
 * Format appointment time with grammatical article and optional range.
 */
export function appointmentTimeText(
  startTime: string,
  endTime?: string,
  options: {
    timeMode?: TimeMode;
    messageTimeMode?: MessageTimeMode;
    connector?: string;
  } = {}
): string {
  const {
    timeMode = '12h',
    messageTimeMode = 'start_only',
    connector = 'hasta',
  } = options;

  const startFormatted = formatTime(startTime, timeMode);
  const article = hourArticle(startFormatted);

  if (messageTimeMode === 'range' && endTime && endTime !== startTime) {
    const endFormatted = formatTime(endTime, timeMode);
    return `${article} ${startFormatted} ${connector} ${endFormatted}`;
  }

  return `${article} ${startFormatted}`;
}

/**
 * Get days between a date and today.
 */
export function getDaysAbsent(dateStr?: string): number {
  if (!dateStr || dateStr === 'Ninguna') return 0;
  return Math.max(0, dayjs().startOf('day').diff(dayjs(dateStr).startOf('day'), 'day'));
}

/**
 * Get days between today and a future date.
 */
export function getDaysUntil(dateStr?: string): number {
  if (!dateStr || dateStr === 'Ninguna') return 0;
  return Math.max(0, dayjs(dateStr).startOf('day').diff(dayjs().startOf('day'), 'day'));
}

/**
 * Build an array of date strings around a center date.
 */
export function buildDatesWindow(centerDate: string, windowDays: number = 0): string[] {
  const center = dayjs(centerDate);
  const dates: string[] = [];
  for (let i = -windowDays; i <= windowDays; i++) {
    dates.push(center.add(i, 'day').format('YYYY-MM-DD'));
  }
  return dates;
}

/**
 * Get today's date as YYYY-MM-DD.
 */
export function today(): string {
  return dayjs().format('YYYY-MM-DD');
}

/**
 * Format a date for export filenames.
 */
export function formatDateForFilename(dateStr?: string): string {
  return dayjs(dateStr || undefined).format('YYYY-MM-DD');
}

/**
 * Format an ISO or string date to standard UI format DD/MM/YY (e.g. 06/05/25) or DD/MM/YYYY.
 */
export function formatDateDDMMYY(dateStr?: string, includeYear4 = false): string {
  if (!dateStr || dateStr === 'Ninguna' || dateStr === 'Registrada') return dateStr || '';
  const d = dayjs(dateStr);
  if (!d.isValid()) return dateStr;
  return d.format(includeYear4 ? 'DD/MM/YYYY' : 'DD/MM/YY');
}
